// src/shell/CommandPalette.tsx
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { useAppStore } from './appStore'
import { api } from '../lib/api'
import type { Creator, Deal, Campaign } from '../lib/types'

type SearchResult =
  | { kind: 'creator'; id: string; label: string; sub: string }
  | { kind: 'deal'; id: string; label: string; sub: string; creatorId: string }
  | { kind: 'campaign'; id: string; label: string; sub: string }

function useCommandSearch(query: string, appId: string | null) {
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!query.trim() || !appId) {
      setResults([])
      return
    }

    setLoading(true)
    const controller = new AbortController()

    Promise.all([
      api.get<{ creators: Creator[] }>('/api/creators').catch(() => ({ creators: [] })),
      api.get<{ deals: Deal[] }>(`/api/deals?appId=${appId}`).catch(() => ({ deals: [] })),
      api.get<{ campaigns: Campaign[] }>(`/api/campaigns?appId=${appId}`).catch(() => ({ campaigns: [] })),
    ]).then(([{ creators }, { deals }, { campaigns }]) => {
      if (controller.signal.aborted) return

      const q = query.toLowerCase()

      const creatorResults: SearchResult[] = creators
        .filter((c) => c.displayName.toLowerCase().includes(q))
        .slice(0, 5)
        .map((c) => ({
          kind: 'creator' as const,
          id: c.id,
          label: c.displayName,
          sub: c.niche ?? c.collaborationType,
        }))

      const dealResults: SearchResult[] = deals
        .filter((d) => {
          const creator = creators.find((c) => c.id === d.creatorId)
          return creator?.displayName.toLowerCase().includes(q) || d.stage.includes(q)
        })
        .slice(0, 5)
        .map((d) => {
          const creator = creators.find((c) => c.id === d.creatorId)
          return {
            kind: 'deal' as const,
            id: d.id,
            label: creator?.displayName ?? d.creatorId,
            sub: d.stage,
            creatorId: d.creatorId,
          }
        })

      const campaignResults: SearchResult[] = campaigns
        .filter((c) => c.name.toLowerCase().includes(q))
        .slice(0, 5)
        .map((c) => ({
          kind: 'campaign' as const,
          id: c.id,
          label: c.name,
          sub: c.status,
        }))

      setResults([...creatorResults, ...dealResults, ...campaignResults])
      setLoading(false)
    })

    return () => controller.abort()
  }, [query, appId])

  return { results, loading }
}

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const appId = useAppStore((s) => s.activeAppId)
  const { results, loading } = useCommandSearch(query, appId)

  // Reset à chaque ouverture
  useEffect(() => {
    if (open) setQuery('')
  }, [open])

  const handleSelect = useCallback(
    (result: SearchResult) => {
      onClose()
      if (result.kind === 'creator' || result.kind === 'deal') {
        const creatorId = result.kind === 'creator' ? result.id : result.creatorId
        navigate(`/crm/contacts/${creatorId}`)
      } else if (result.kind === 'campaign') {
        navigate('/crm/campaigns')
      }
    },
    [navigate, onClose],
  )

  if (!open) return null

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 9998,
        }}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        data-testid="cmdk-palette"
        role="dialog"
        aria-label="Palette de commandes"
        style={{
          position: 'fixed',
          top: '20vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(580px, 90vw)',
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        <Command label="Recherche globale">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid var(--line)',
              gap: '0.5rem',
            }}
          >
            <span style={{ color: 'var(--dim)', fontSize: '1rem' }}>🔍</span>
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Rechercher créateurs, deals, campagnes…"
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                outline: 'none',
                color: 'var(--fg)',
                fontSize: '0.9375rem',
                fontFamily: 'inherit',
              }}
              autoFocus
            />
            <kbd
              style={{
                fontSize: '0.6875rem',
                color: 'var(--dim)',
                border: '1px solid var(--line)',
                borderRadius: '4px',
                padding: '0.1rem 0.375rem',
                fontFamily: 'var(--mono)',
              }}
            >
              Esc
            </kbd>
          </div>

          <Command.List
            style={{
              maxHeight: '360px',
              overflowY: 'auto',
              padding: '0.375rem 0',
            }}
          >
            {loading && (
              <Command.Loading>
                <div style={{ padding: '0.75rem 1rem', color: 'var(--dim)', fontSize: '0.875rem' }}>
                  Recherche…
                </div>
              </Command.Loading>
            )}

            {!loading && query.trim() && results.length === 0 && (
              <Command.Empty>
                <div style={{ padding: '0.75rem 1rem', color: 'var(--dim)', fontSize: '0.875rem' }}>
                  Aucun résultat pour « {query} »
                </div>
              </Command.Empty>
            )}

            {!loading && results.length > 0 && (
              <>
                {results.some((r) => r.kind === 'creator') && (
                  <Command.Group
                    heading="Créateurs"
                    style={{ padding: '0 0 0.25rem' }}
                  >
                    {results
                      .filter((r): r is Extract<SearchResult, { kind: 'creator' }> => r.kind === 'creator')
                      .map((r) => (
                        <Command.Item
                          key={`creator-${r.id}`}
                          value={`creator-${r.id}-${r.label}`}
                          onSelect={() => handleSelect(r)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.625rem',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            margin: '0 0.375rem',
                            fontSize: '0.875rem',
                            color: 'var(--fg)',
                          }}
                        >
                          <span style={{ fontSize: '0.75rem', color: 'var(--muted)', minWidth: '60px' }}>Créateur</span>
                          <span style={{ fontWeight: 500 }}>{r.label}</span>
                          <span style={{ color: 'var(--dim)', fontSize: '0.8125rem', marginLeft: 'auto' }}>{r.sub}</span>
                        </Command.Item>
                      ))}
                  </Command.Group>
                )}

                {results.some((r) => r.kind === 'deal') && (
                  <Command.Group heading="Deals">
                    {results
                      .filter((r): r is Extract<SearchResult, { kind: 'deal' }> => r.kind === 'deal')
                      .map((r) => (
                        <Command.Item
                          key={`deal-${r.id}`}
                          value={`deal-${r.id}-${r.label}`}
                          onSelect={() => handleSelect(r)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.625rem',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            margin: '0 0.375rem',
                            fontSize: '0.875rem',
                            color: 'var(--fg)',
                          }}
                        >
                          <span style={{ fontSize: '0.75rem', color: 'var(--muted)', minWidth: '60px' }}>Deal</span>
                          <span style={{ fontWeight: 500 }}>{r.label}</span>
                          <span style={{ color: 'var(--dim)', fontSize: '0.8125rem', marginLeft: 'auto' }}>{r.sub}</span>
                        </Command.Item>
                      ))}
                  </Command.Group>
                )}

                {results.some((r) => r.kind === 'campaign') && (
                  <Command.Group heading="Campagnes">
                    {results
                      .filter((r): r is Extract<SearchResult, { kind: 'campaign' }> => r.kind === 'campaign')
                      .map((r) => (
                        <Command.Item
                          key={`campaign-${r.id}`}
                          value={`campaign-${r.id}-${r.label}`}
                          onSelect={() => handleSelect(r)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.625rem',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            margin: '0 0.375rem',
                            fontSize: '0.875rem',
                            color: 'var(--fg)',
                          }}
                        >
                          <span style={{ fontSize: '0.75rem', color: 'var(--muted)', minWidth: '60px' }}>Campagne</span>
                          <span style={{ fontWeight: 500 }}>{r.label}</span>
                          <span style={{ color: 'var(--dim)', fontSize: '0.8125rem', marginLeft: 'auto' }}>{r.sub}</span>
                        </Command.Item>
                      ))}
                  </Command.Group>
                )}
              </>
            )}

            {!query.trim() && (
              <div style={{ padding: '0.75rem 1rem', color: 'var(--dim)', fontSize: '0.8125rem' }}>
                Tapez pour rechercher des créateurs, deals ou campagnes…
              </div>
            )}
          </Command.List>
        </Command>
      </div>
    </>
  )
}
